import { useEffect, useState } from "react";
import { Config } from "~/constants/configuration_provider";
import {
  computeIncidentState,
  IncidentIndicator,
  parseIncidentComponents,
  type IncidentState,
  type SummaryResponse,
} from "~/components/IncidentBanner/incidentTargeting";
import { resolveActiveRegionName } from "~/providers/RegionsProvider";

export const MONDOO_STATUS_PAGE_URL = "https://status.mondoo.com/";
// summary.json (vs the older status.json) carries components, incidents, and
// scheduled_maintenances alongside the global status rollup — everything needed
// to target the banner to this deployment's watched components.
const SUMMARY_PAGE_URL = "https://mondoo.statuspage.io/api/v2/summary.json";
export const INCIDENT_BANNER_HEIGHT = 53;

// Components this deployment watches, declared via the public env allowlist
// (component names or ids). Empty = not configured → the global status rollup
// (today's behavior). VITE_INCIDENT_COMPONENTS is injected at runtime via
// app.config.js (window.AppConfig) and falls back to the build-time value, so
// reading it once at module scope matches how other VITE_ config is consumed.
const INCIDENT_COMPONENTS = parseIncidentComponents(
  Config.VITE_INCIDENT_COMPONENTS ?? undefined,
);

// Re-exported so existing consumers can keep importing IncidentIndicator /
// IncidentState from this module unchanged. The canonical definitions live in
// incidentTargeting.ts.
export { IncidentIndicator };
export type { IncidentState };

// Hardcoded message used only on the fallback path (no allowlist configured).
// When the banner is targeted, the real incident name from the feed is shown
// instead.
function formatIncidentMessage(indicator: IncidentIndicator): string {
  switch (indicator) {
    case IncidentIndicator.MAINTENANCE:
      return "Mondoo is currently undergoing maintenance";
    default:
      return "There is an ongoing incident on Mondoo";
  }
}

const incidentInfoInitialState: IncidentState = {
  msg: "",
  indicator: IncidentIndicator.NONE,
  open: false,
  fetched: false,
};

export const useIncidentBanner = () => {
  const [incident, setIncident] = useState<IncidentState>(
    incidentInfoInitialState,
  );
  const [showIncidentBanner, setShowIncidentBanner] = useState<boolean>(true);

  const onCloseIncidentBanner = () => setShowIncidentBanner(false);

  // The user's active region (e.g. "US"/"EU") scopes region-prefixed allowlist
  // entries so the multi-region SaaS console only shows a region's incident to
  // that region's users. This hook is mounted in the auth provider, ABOVE
  // RegionsProvider, so the region context isn't reachable here — read the
  // persisted region directly (same source the provider uses). Undefined
  // pre-login / single-region, where only un-scoped entries (global, private-*)
  // apply. Resolved once at mount; an in-place region switch updates the banner
  // on the next reload, which is acceptable for this legacy console.
  const activeRegion = resolveActiveRegionName();

  useEffect(() => {
    const abortController = new AbortController();

    fetch(SUMMARY_PAGE_URL, { signal: abortController.signal })
      .then((response) => {
        // A non-2xx (e.g. a 503 with a JSON error body during a widespread
        // outage) shouldn't be fed to computeIncidentState. Throw so the
        // .catch below keeps the banner hidden.
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json() as Promise<SummaryResponse>;
      })
      .then((data) => {
        const next = computeIncidentState(data, {
          allowlistEntries: INCIDENT_COMPONENTS,
          activeRegion,
          formatFallbackMessage: formatIncidentMessage,
        });
        setIncident(next);
        setShowIncidentBanner(next.open);
      })
      .catch((error: unknown) => {
        // Don't log abort errors
        if (error instanceof Error && error.name !== "AbortError") {
          console.warn(
            "[IncidentBanner] Failed to fetch status:",
            error.message,
          );
        }
        // Keep default state (no incident banner)
        setIncident((prevState) => ({
          ...prevState,
          fetched: true,
        }));
      });

    return () => {
      abortController.abort();
    };
    // activeRegion feeds computeIncidentState; included so a change re-derives
    // banner state. The fetched summary.json is region-agnostic (one global
    // feed), so the region only affects the pure compute, not what's fetched.
  }, [activeRegion]);

  return {
    incident,
    showIncidentBanner,
    onCloseIncidentBanner,
  };
};
